import { defineComponent } from "vue";
import { isNullOrEmpty } from "../../skyward.base.extensions";
import { VueContext } from "../../types";
import { InternalVueConstructor } from "../../types/vue/vueConstructor";
import { ClassNamesCollection, createAppWithRootComponent } from "../core/vueHelpers";
import DataPart from "./components/DataPart";
import LabeledData from "./components/LabeledData";
import LabeledDataList, { LabeledDataListProps } from "./components/LabeledDataList";
import LabelPart from "./components/LabelPart";

interface LabeledDataPartData
{
    id?: string;
    class?: ClassNamesCollection;
    text?: string;
    html?: string;
}

interface LabeledDataData
{
    id?: string;
    class?: ClassNamesCollection;
    inline?: boolean;
    labelPart: LabeledDataPartData;
    dataPart: LabeledDataPartData;
}

export interface LabeledDataListData extends LabeledDataListProps
{
    id?: string;
    class?: ClassNamesCollection;
    labeledData?: LabeledDataData[];
}

export interface LabeledDataListContext extends VueContext
{
    props: LabeledDataListData;
}

// Converts the labeled data serialized by the server into children, so the components only ever take children.
const LabeledDataListConstructor: InternalVueConstructor<LabeledDataListContext> =
{
    buildVue: function (context)
    {
        const { labeledData, ...listProps } = context.props;

        const root = defineComponent({
            setup()
            {
                return () => (
                    <LabeledDataList {...listProps}>
                        {
                            labeledData?.map(({ labelPart, dataPart, ...labeledDataProps }, index) =>
                            {
                                const { text: labelText, ...labelPartProps } = labelPart;
                                const { text: dataText, ...dataPartProps } = dataPart;

                                return (
                                    <LabeledData key={index} {...labeledDataProps}>
                                        <LabelPart {...labelPartProps}>{labelText}</LabelPart>
                                        <DataPart {...dataPartProps}>{isNullOrEmpty(dataText) ? null : dataText}</DataPart>
                                    </LabeledData>
                                );
                            })
                        }
                    </LabeledDataList>
                );
            }
        });

        return createAppWithRootComponent(root)
            .mount(context.container);
    }
};

export default LabeledDataListConstructor;
